/*
  API Client com fetch, tratamento de erros padrão e suporte a token.
*/

export class HttpError extends Error {
  status: number;
  data?: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  headers?: Record<string, string>;
  // Aceita qualquer objeto, permitindo passar filtros tipados sem index signature
  query?: unknown;
  body?: unknown;
}

function buildQuery(query?: unknown): string {
  if (!query) return '';
  const entries = Object.entries(query as Record<string, unknown>);
  const params = entries
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return params ? `?${params}` : '';
}

async function safeJson(res: Response): Promise<any> {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : undefined;
  } catch {
    return text; // retorna texto bruto se não for JSON
  }
}

export class ApiClient {
  private static baseUrl = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) || '/api/v1';
  private static token: string | null = null;

  static setBaseUrl(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
  }

  static getBaseUrl() {
    return this.baseUrl;
  }

  static setToken(token: string | null) {
    this.token = token;
  }

  static getToken() {
    return this.token;
  }

  private static async request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${path}${buildQuery(options.query)}`;
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const res = await fetch(url, {
      method,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'include',
    });

    const data = await safeJson(res);
    if (!res.ok) {
      const message = (data && (data.message || data.error)) || res.statusText || 'Erro na requisição';
      throw new HttpError(res.status, String(message), data);
    }
    return data as T;
  }

  static get<T>(path: string, options?: RequestOptions) {
    return this.request<T>('GET', path, options);
  }
  static post<T>(path: string, options?: RequestOptions) {
    return this.request<T>('POST', path, options);
  }
  static put<T>(path: string, options?: RequestOptions) {
    return this.request<T>('PUT', path, options);
  }
  static patch<T>(path: string, options?: RequestOptions) {
    return this.request<T>('PATCH', path, options);
  }
  static delete<T>(path: string, options?: RequestOptions) {
    return this.request<T>('DELETE', path, options);
  }
}